import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React, { ReactNode } from 'react';

// Ported from `packages/frontend/src/pages/components/assurance/AssuranceSummaryCard.tsx`
// so the admin Statistics tab can match the dashboard's Metro-tile look
// without depending on theme tokens that resolve to white in the admin
// palette (the admin theme maps `text.primary` to "text-on-accent"). This
// component renders explicit white text on a saturated background, so it
// is immune to that.

export interface SummaryCardBreakdownRow {
  readonly label: string;
  readonly value: ReactNode;
}

interface SummaryCardProps {
  readonly title: ReactNode;
  readonly value: ReactNode;
  readonly breakdown?: readonly SummaryCardBreakdownRow[];
  readonly footer?: ReactNode;
  readonly loading?: boolean;
  readonly color?: string;
}

export function SummaryCard({
  title,
  value,
  breakdown,
  footer,
  loading = false,
  color = '#37474F'
}: SummaryCardProps) {
  const hasBreakdown = breakdown != null && breakdown.length > 0;
  // Compact mode: when there's nothing to render below the headline number
  // we collapse the tile so the empty lower half doesn't read as dead space.
  // The tile still scales to its grid cell width — only the vertical size
  // shrinks.
  const compact = !loading && !hasBreakdown && footer == null;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        backgroundColor: color,
        color: '#fff',
        px: 4,
        py: compact ? 3 : 4,
        borderRadius: '2px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: compact ? 'center' : 'flex-start',
        minHeight: compact ? 110 : 160,
        transition: 'transform 120ms ease, filter 120ms ease',
        '&:hover': {
          filter: 'brightness(1.05)'
        }
      }}
    >
      <Typography
        variant="labelXSmall"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
          opacity: 0.9,
          color: 'inherit',
          mb: compact ? 1 : 2
        }}
      >
        {title}
      </Typography>

      {loading ? (
        <Skeleton
          variant="text"
          width="60%"
          sx={{ fontSize: '2.5rem', bgcolor: 'rgba(255,255,255,0.2)' }}
        />
      ) : (
        <Typography
          component="div"
          sx={{
            color: 'inherit',
            fontWeight: 300,
            fontSize: '2.75rem',
            lineHeight: 1,
            mb: compact ? 0 : 3
          }}
        >
          {value}
        </Typography>
      )}

      {!compact && <Box sx={{ flex: 1 }} />}

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="text" width="80%" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
          <Skeleton variant="text" width="65%" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        </Stack>
      ) : hasBreakdown ? (
        <Stack spacing={1}>
          {breakdown!.map((row, index) => (
            <Stack
              key={`${row.label}-${index}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography
                variant="labelXSmall"
                sx={{
                  color: 'inherit',
                  opacity: 0.9,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {row.label}
              </Typography>
              <Typography variant="labelXSmall" sx={{ color: 'inherit', fontWeight: 700 }}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}

      {footer != null && (
        <Typography variant="labelXSmall" sx={{ color: 'inherit', opacity: 0.7, mt: 2 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
}
